"use client";

import { c as _c } from "react/compiler-runtime";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@szum-tech/design-system";
import { MonitorIcon, MoonIcon, SunIcon } from "lucide-react";
import { useTheme } from "next-themes";
import * as React from "react";
type Theme = "system" | "light" | "dark";
const THEME_CYCLE: Array<Theme> = ["system", "light", "dark"];
const THEME_LABELS: Record<Theme, string> = {
  dark: "Dark theme",
  light: "Light theme",
  system: "System theme"
};
function isTheme(value: string | undefined): value is Theme {
  return THEME_CYCLE.some(theme => theme === value);
}

/**
 * Theme toggle button component that cycles through system, light, and dark themes.
 *
 * Features:
 * - Three states: System -> Light -> Dark -> System
 * - Accessible with proper aria-labels
 * - Keyboard navigable
 * - Shows appropriate icon for current theme
 * - Tooltip showing current theme name
 *
 * @example
 * ```tsx
 * <ThemeToggle />
 * ```
 */
export function ThemeToggle() {
  const $ = _c(28);
  const {
    theme,
    setTheme
  } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setMounted(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  React.useEffect(t0, t1);
  let t2;
  if ($[2] !== theme) {
    t2 = isTheme(theme) ? theme : "system";
    $[2] = theme;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const currentTheme = t2;
  let t3;
  if ($[4] !== currentTheme || $[5] !== setTheme) {
    t3 = () => {
      const currentIndex = THEME_CYCLE.indexOf(currentTheme);
      const nextIndex = (currentIndex + 1) % THEME_CYCLE.length;
      const nextTheme = THEME_CYCLE[nextIndex];
      if (nextTheme) {
        setTheme(nextTheme);
      }
    };
    $[4] = currentTheme;
    $[5] = setTheme;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const cycleTheme = t3;
  let t4;
  if ($[7] !== cycleTheme) {
    t4 = event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        cycleTheme();
      }
    };
    $[7] = cycleTheme;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleKeyDown = t4;
  if (!mounted) {
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Button aria-label="Toggle theme" disabled={true} size="icon" variant="ghost"><span className="size-4" /></Button>;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  let t5;
  if ($[10] !== currentTheme) {
    t5 = () => {
      if (currentTheme === "system") {
        return <MonitorIcon aria-hidden="true" className="size-4" />;
      }
      if (currentTheme === "light") {
        return <SunIcon aria-hidden="true" className="size-4" />;
      }
      return <MoonIcon aria-hidden="true" className="size-4" />;
    };
    $[10] = currentTheme;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const renderIcon = t5;
  let t6;
  if ($[12] !== currentTheme) {
    t6 = () => {
      const currentIndex_0 = THEME_CYCLE.indexOf(currentTheme);
      const nextIndex_0 = (currentIndex_0 + 1) % THEME_CYCLE.length;
      const nextTheme_0 = THEME_CYCLE[nextIndex_0];
      return nextTheme_0 ? THEME_LABELS[nextTheme_0] : THEME_LABELS.system;
    };
    $[12] = currentTheme;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const getNextThemeLabel = t6;
  let t7;
  if ($[14] !== getNextThemeLabel) {
    t7 = getNextThemeLabel();
    $[14] = getNextThemeLabel;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = `Current: ${THEME_LABELS[currentTheme]}. Click to switch to ${t7}`;
  let t9;
  if ($[16] !== renderIcon) {
    t9 = renderIcon();
    $[16] = renderIcon;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== cycleTheme || $[19] !== handleKeyDown || $[20] !== t8 || $[21] !== t9) {
    t10 = <TooltipTrigger asChild={true}><Button aria-label={t8} onClick={cycleTheme} onKeyDown={handleKeyDown} size="icon" variant="ghost">{t9}</Button></TooltipTrigger>;
    $[18] = cycleTheme;
    $[19] = handleKeyDown;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = THEME_LABELS[currentTheme];
  let t12;
  if ($[23] !== t11) {
    t12 = <TooltipContent side="bottom"><p>{t11}</p></TooltipContent>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t12) {
    t13 = <Tooltip>{t10}{t12}</Tooltip>;
    $[25] = t10;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
}